import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Sparkles,
    Wind,
    Users,
    BookOpen,
    Layers,
    Music,
    Clock,
    Globe,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { SERVICES, BOOKABLE_SERVICES, IMAGES } from "../data/content";
import SectionHeading from "../components/SectionHeading";
import useReveal from "../hooks/useReveal";

const ICONS = { Sparkles, Wind, Users, BookOpen, Layers, Music };

export default function ServicesPage() {
    useReveal();
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.replace("#", ""));
            if (el) {
                setTimeout(
                    () =>
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        }),
                    180,
                );
            }
        }
    }, [hash]);

    return (
        <div className="pt-24" data-testid="services-page">
            {/* HEADER */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                    <div className="lg:col-span-7">
                        <SectionHeading
                            eyebrow="Services"
                            title="A practice woven from"
                            titleItalic="six gentle modalities."
                            sub="Every session is held with care, slowness, and devotion. Choose a path that calls to you — or write to us, and we'll find it together."
                        />
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[5/4] overflow-hidden">
                            <img
                                src={IMAGES.soundHealing}
                                alt="Sound healing bowls"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* SESSIONS & PRICING */}
            <section
                className="bg-white border-t border-[#EFEBE4]"
                data-testid="sessions-pricing"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
                    <div className="ssh-reveal">
                        <SectionHeading
                            eyebrow="Sessions & Pricing"
                            title="Simple, transparent"
                            titleItalic="ways to begin."
                            sub="Choose a session, pick a date and time that suits you, and confirm instantly with secure online payment."
                        />
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#EFEBE4] border border-[#EFEBE4]">
                        {BOOKABLE_SERVICES.map((s, i) => (
                            <article
                                key={s.slug}
                                className="bg-white p-10 lg:p-12 flex flex-col ssh-reveal"
                                style={{ transitionDelay: `${0.05 * i}s` }}
                                data-testid={`pricing-${s.slug}`}
                            >
                                <p className="ssh-eyebrow">{s.eyebrow}</p>
                                <h3 className="mt-5 font-serif text-2xl lg:text-3xl text-[#2C2B29] leading-tight">
                                    {s.name}
                                </h3>
                                <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[#8A9A86]">
                                    {s.duration}
                                </p>
                                <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                                    <span className="font-serif text-4xl leading-none text-[#2C2B29]">
                                        {s.priceLabel}
                                    </span>
                                    {s.savingsLabel && (
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#A68A6B]">
                                            {s.valueLabel} · {s.savingsLabel}
                                        </span>
                                    )}
                                </div>
                                <p className="mt-5 text-sm text-[#4A4947] leading-relaxed">
                                    {s.short}
                                </p>
                                <div className="mt-auto pt-8">
                                    <Link
                                        to={`/contact?service=${s.slug}#booking`}
                                    >
                                        <Button
                                            className="ssh-btn-primary rounded-none px-7 py-6 text-xs uppercase tracking-[0.22em]"
                                            data-testid={`pricing-book-${s.slug}`}
                                        >
                                            Book & Pay {s.priceLabel}
                                        </Button>
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES LIST */}
            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    {SERVICES.map((s, i) => {
                        const Icon = ICONS[s.icon];
                        const isAlt = i % 2 === 1;
                        return (
                            <article
                                key={s.slug}
                                id={s.slug}
                                className="border-b border-[#EFEBE4] py-20 lg:py-28 scroll-mt-24"
                                data-testid={`service-${s.slug}`}
                            >
                                <div
                                    className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start ssh-reveal ${
                                        isAlt ? "" : ""
                                    }`}
                                >
                                    <div className="lg:col-span-4">
                                        <Icon
                                            className="h-9 w-9 text-[#A68A6B]"
                                            strokeWidth={1.25}
                                        />
                                        <p className="ssh-eyebrow mt-6">
                                            0{i + 1} · Modality
                                        </p>
                                        <h2 className="mt-4 font-serif font-light tracking-tight leading-[1.05] text-4xl lg:text-5xl text-[#2C2B29]">
                                            {s.name}
                                        </h2>
                                    </div>
                                    <div className="lg:col-span-8">
                                        <p className="text-lg leading-relaxed text-[#4A4947]">
                                            {s.short}
                                        </p>
                                        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                                            <div>
                                                <p className="ssh-eyebrow">
                                                    What it helps with
                                                </p>
                                                <ul className="mt-5 space-y-3 text-base text-[#2C2B29]">
                                                    {s.helps.map((h) => (
                                                        <li
                                                            key={h}
                                                            className="flex gap-3"
                                                        >
                                                            <span className="text-[#A68A6B]">
                                                                —
                                                            </span>
                                                            <span>{h}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div>
                                                <p className="ssh-eyebrow">
                                                    Session process
                                                </p>
                                                <p className="mt-5 text-base leading-relaxed text-[#4A4947]">
                                                    {s.process}
                                                </p>
                                                <div className="mt-8 flex flex-col gap-3 text-sm text-[#4A4947]">
                                                    <span className="inline-flex items-center gap-2">
                                                        <Clock
                                                            className="h-4 w-4 text-[#A68A6B]"
                                                            strokeWidth={1.5}
                                                        />
                                                        {s.duration}
                                                    </span>
                                                    <span className="inline-flex items-center gap-2">
                                                        <Globe
                                                            className="h-4 w-4 text-[#A68A6B]"
                                                            strokeWidth={1.5}
                                                        />
                                                        {s.availability}
                                                    </span>
                                                </div>
                                                <div className="mt-8">
                                                    <Link to="/contact#booking">
                                                        <Button
                                                            className="ssh-btn-primary rounded-none px-7 py-6 text-xs uppercase tracking-[0.22em]"
                                                            data-testid={`book-${s.slug}`}
                                                        >
                                                            Book {s.name}
                                                        </Button>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
